<!-- Copyright: Seán I. O'Donoghue -->

<template>
  <main
    ref="mainRef"
    class="modal-main"
    :tabindex="hasFocusableContent ? -1 : undefined"
    @scroll="handleMainScroll">
    <slot />
  </main>
</template>

<script setup>
import {inject, onMounted, ref} from "vue";
import {useMutationObserver} from "@vueuse/core";

// Tab doesn't stop on the scroll area itself when it holds controls (2026-10-14): Firefox makes
// every scrollable area a Tab stop, a stop that does nothing (e.g. #profile's Events tab before
// its Invoice/Receipt link); the keys still scroll it from those controls. With none, it stays a
// stop, so a keyboard can still scroll it (Chrome's own rule)
const FOCUSABLE = "a[href], button, input, select, textarea, [tabindex]:not([tabindex=\"-1\"])";

const handleMainScroll = inject("handleMainScroll", () => {});
const mainRef = ref(null);
const hasFocusableContent = ref(false);

function updateHasFocusableContent() {
  hasFocusableContent.value = Boolean(mainRef.value?.querySelector(FOCUSABLE));
}

onMounted(updateHasFocusableContent);
useMutationObserver(mainRef, updateHasFocusableContent, {childList: true, subtree: true});
</script>

<style scoped>
.modal-main {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  /* room for a field's focus shadow (3px), which this overflow clips at the sides and below
     the last field; the margins keep the content where it was (0.5rem below, as before) */
  padding-inline: 4px;
  margin-inline: -4px;
  padding-bottom: 4px;
  margin-bottom: calc(0.5rem - 4px);
  /* no overscroll-behavior: contain (2026-10-22): zoomed in until the dialog filled the screen,
     it stopped every pan; the page behind is held by the body's overflow: hidden (index.vue) */
  scrollbar-width: thin;
}
</style>
